import { useEffect } from "react";
import {
  openAppWindow,
  platformPrimaryModifier,
  prewarmAppWindow,
  resolveNewWindowLink,
} from "src/lib/runtime/appWindows";
import { currentWindowKind, isMainWindow, isTauriRuntime } from "src/lib/runtime/environment";

/** `requestIdleCallback` falls back to a timer on Safari, which is what WKWebView is. */
const onIdle = (callback: () => void) => {
  const idle = (window as typeof window & { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (idle) idle(callback);
  else window.setTimeout(callback, 2000);
};

/**
 * Cmd+click (Ctrl+click off macOS) and middle-click open the link in a new desktop window.
 *
 * In a browser the webview would hand these to the browser's own new-tab behaviour, and react-router
 * deliberately steps aside for modified clicks so it can. The desktop app has no tabs and no
 * new-window handler, so the click lands nowhere — this listener catches it first and opens the
 * route as its own window instead.
 *
 * The listener is on the document in the capture phase so it beats React's own delegated handlers:
 * plenty of links in the app sit inside rows that navigate on click, and those must not fire too.
 */
export function useNewWindowLinks(): void {
  useEffect(() => {
    if (!isTauriRuntime()) return;

    // One warm window is enough for the app: whoever opens it claims the shared slot and the open
    // replenishes it, so only the first one needs starting and the main window does it.
    if (isMainWindow()) {
      onIdle(
        () =>
          void prewarmAppWindow().catch((error) =>
            console.warn("Could not prewarm an application window.", error)
          )
      );
    }

    const primaryModifier = platformPrimaryModifier();
    const handleClick = (event: MouseEvent) => {
      const path = resolveNewWindowLink(event, { currentUrl: window.location.href, primaryModifier });
      if (!path) return;

      event.preventDefault();
      event.stopPropagation();
      void openAppWindow(path).catch((error) => console.warn("Could not open a new window.", error));
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("auxclick", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("auxclick", handleClick, true);
    };
  }, []);
}

/**
 * Keep an extra application window's native title on the page it is showing.
 *
 * Tauri does not mirror `document.title`, and the main window never needs it — its title is fixed in
 * the Tauri config. A person with four deal windows open needs the Window menu and Mission Control
 * to tell them apart, so those windows rename themselves as they navigate.
 */
export function useAppWindowTitle(): void {
  useEffect(() => {
    if (currentWindowKind() !== "application") return;

    let cancelled = false;
    const applyTitle = async () => {
      const title = document.title;
      const { getCurrentWindow } = await import("@tauri-apps/api/window");
      if (cancelled) return;
      await getCurrentWindow()
        .setTitle(title)
        .catch(() => undefined);
    };

    // Titles are set by route-level effects all over the app, so watch the element rather than
    // trying to subscribe to every place that writes one.
    const titleElement = document.querySelector("title");
    const observer = new MutationObserver(() => void applyTitle());
    if (titleElement) {
      observer.observe(titleElement, { childList: true, characterData: true, subtree: true });
    }
    void applyTitle();

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);
}
